
import { useMemo, useState } from 'react';
import { MasteryRecord } from '../../../hooks/useQuizMasteryDB';

interface QuizQuestion {
  id: string;
  subject: string;
  subcategory: string | null;
}

interface BookmarkBreakdownProps {
  questions: QuizQuestion[];
  masteryRecord: MasteryRecord;
}

interface SubjectBreakdown {
  subject: string;
  count: number;
  subcategories: { name: string; count: number }[];
}

/**
 * BookmarkBreakdown component
 *
 * Shows a breakdown of bookmarked (i.e. “苦手”) questions by subject
 * and, when applicable, by sub‑category.
 *
 * The component is defensive against missing data:
 *  • If `questions` or `masteryRecord` is undefined/null it treats them as empty.
 *  • If a question does not have a sub‑category the logic simply skips it.
 *  • All numeric calculations guard against division by zero.
 */
export default function BookmarkBreakdown({
  questions = [],
  masteryRecord = {},
}: BookmarkBreakdownProps) {
  const [expandedSubject, setExpandedSubject] = useState<string | null>(null);

  const breakdownData = useMemo(() => {
    // Guard against malformed inputs
    if (!Array.isArray(questions) || typeof masteryRecord !== 'object' || masteryRecord === null) {
      return { total: 0, subjects: [] as SubjectBreakdown[] };
    }

    const bookmarkedQuestions = questions.filter(
      (q) => masteryRecord[q.id]?.is_bookmarked === true
    );

    if (bookmarkedQuestions.length === 0) {
      return { total: 0, subjects: [] as SubjectBreakdown[] };
    }

    const subjectMap: Record<
      string,
      { count: number; subcatMap: Record<string, number> }
    > = {};

    bookmarkedQuestions.forEach((q) => {
      if (!subjectMap[q.subject]) {
        subjectMap[q.subject] = { count: 0, subcatMap: {} };
      }
      subjectMap[q.subject].count += 1;
      if (q.subcategory) {
        const subMap = subjectMap[q.subject].subcatMap;
        subMap[q.subcategory] = (subMap[q.subcategory] ?? 0) + 1;
      }
    });

    const subjects: SubjectBreakdown[] = Object.entries(subjectMap)
      .map(([subject, data]) => ({
        subject,
        count: data.count,
        subcategories: Object.entries(data.subcatMap)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count),
      }))
      .sort((a, b) => b.count - a.count);

    return { total: bookmarkedQuestions.length, subjects };
  }, [questions, masteryRecord]);

  // Nothing to display
  if (breakdownData.total === 0) {
    return null;
  }

  const maxCount =
    breakdownData.subjects.length > 0 ? breakdownData.subjects[0].count : 1;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <header className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl shrink-0">
            <i className="ri-bookmark-fill text-lg text-white"></i>
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-800">
              苦手チェック内訳
              <span className="ml-2 text-xs font-normal text-orange-500">
                合計 {breakdownData.total}問
              </span>
            </h3>
            <p className="text-xs text-gray-400">科目・カテゴリ別の苦手問題数</p>
          </div>
        </div>
      </header>

      <section className="p-4 space-y-2">
        {breakdownData.subjects.map((item) => {
          const isExpanded = expandedSubject === item.subject;
          const hasSubcats = item.subcategories.length > 0;
          const barWidth = Math.max((item.count / maxCount) * 100, 8);
          const percentage = Math.round((item.count / breakdownData.total) * 100);

          const handleClick = () => {
            if (hasSubcats) {
              setExpandedSubject(isExpanded ? null : item.subject);
            }
          };

          return (
            <div key={item.subject}>
              <div
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg
                  ${hasSubcats ? 'cursor-pointer hover:bg-gray-50 hover:-translate-y-0.5 hover:shadow-sm' : ''}
                `}
                onClick={handleClick}
              >
                <span className="text-xs font-medium text-gray-700 w-24 sm:w-32 shrink-0 truncate">
                  {item.subject}
                </span>
                <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden relative">
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all duration-500"
                    style={{ width: `${barWidth}%` }}
                  ></div>
                </div>
                <span className="text-xs font-bold text-orange-600 w-10 text-right shrink-0">
                  {item.count}問
                </span>
                <span className="text-xs text-gray-400 w-10 text-right shrink-0">
                  {percentage}%
                </span>
                {hasSubcats && (
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i
                      className={`
                        ri-arrow-down-s-line text-sm text-gray-400
                        transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}
                      `}
                    ></i>
                  </div>
                )}
              </div>

              {/* Subcategory breakdown */}
              {isExpanded && hasSubcats && (
                <div className="ml-6 sm:ml-10 mt-1 mb-2 space-y-1 pl-3 border-l-2 border-orange-200">
                  {item.subcategories.map((subcat) => {
                    const subcatBarWidth = Math.max(
                      (subcat.count / item.count) * 100,
                      10
                    );
                    return (
                      <div
                        key={subcat.name}
                        className="flex items-center gap-2 px-2 py-1.5"
                      >
                        <span className="text-xs text-gray-500 w-28 sm:w-36 shrink-0 truncate">
                          {subcat.name}
                        </span>
                        <div className="flex-1 h-3.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-amber-300 to-orange-400 rounded-full transition-all duration-500"
                            style={{ width: `${subcatBarWidth}%` }}
                          ></div>
                        </div>
                        <span className="text-xs font-medium text-orange-500 w-8 text-right shrink-0">
                          {subcat.count}問
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </section>
    </div>
  );
}
